import axios from 'axios';

// Types for Ghost blog posts
export interface GhostPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  html: string; // Full post content in HTML format
  feature_image: string | null | undefined;
  feature_image_caption?: string; // Caption for the feature image
  published_at: string;
  updated_at?: string;
  url: string;
  visibility?: 'public' | 'members' | 'paid' | 'tiers'; // Ghost visibility levels
  access?: boolean; // Whether current user has access to this content
  primary_author: {
    name: string;
    profile_image: string | null | undefined;
    slug?: string;
  };
  reading_time: number;
  primary_tag?: {
    name: string;
    slug?: string;
  };
  tags?: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
}

export interface GhostTag {
  id: string;
  name: string;
  slug: string;
  description?: string;
  feature_image?: string | null;
  count?: { posts: number };
}

export interface GhostApiResponse {
  posts: GhostPost[];
  meta?: {
    pagination?: {
      page: number;
      limit: number;
      pages: number;
      total: number;
      next?: number;
      prev?: number;
    }
  };
}

// Fallback blog posts to use when API connection fails
const fallbackPosts: GhostPost[] = [
  {
    id: 'fallback-1',
    title: 'Ako začať s umelou inteligenciou v roku 2025',
    slug: 'ako-zacat-s-umelou-inteligenciou',
    excerpt: 'Umelá inteligencia sa stáva čoraz dostupnejšou technológiou. V tomto článku sa dozviete, ako začať využívať AI bez technických znalostí...',
    html: '<p>Umelá inteligencia sa stáva čoraz dostupnejšou technológiou. V tomto článku sa dozviete, ako začať využívať AI bez technických znalostí...</p><p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nulla facilisi. Sed euismod, nisl nec aliquam tincidunt, nunc nisl aliquam nisl, eget aliquam nisl nisl sit amet nisl.</p>',
    feature_image: 'https://images.unsplash.com/photo-1591453089816-0fbb971b454c?q=80&w=1000&auto=format&fit=crop',
    published_at: new Date().toISOString(),
    url: 'https://blog.ai-kurzy.online/ako-zacat-s-umelou-inteligenciou',
    primary_author: {
      name: 'Marián Čuprík',
      profile_image: null,
      slug: 'marian'
    },
    reading_time: 5,
    primary_tag: {
      name: 'AI Trendy',
      slug: 'ai-trendy'
    },
    visibility: 'public'
  },
  {
    id: 'fallback-2',
    title: 'Praktické využitie ChatGPT v každodennej práci',
    slug: 'prakticke-vyuzitie-chatgpt',
    excerpt: 'ChatGPT môže výrazne zvýšiť vašu produktivitu. Zistite, ako ho efektívne využívať pri písaní textov, programovaní a riešení problémov...',
    html: '<p>ChatGPT môže výrazne zvýšiť vašu produktivitu. Zistite, ako ho efektívne využívať pri písaní textov, programovaní a riešení problémov...</p><p>ChatGPT je pokročilý jazykový model vyvinutý spoločnosťou OpenAI, ktorý dokáže generovať text, odpovedať na otázky a pomáhať s rôznymi úlohami.</p>',
    feature_image: 'https://images.unsplash.com/photo-1677442135966-736432af954a?q=80&w=1000&auto=format&fit=crop',
    published_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    url: 'https://blog.ai-kurzy.online/prakticke-vyuzitie-chatgpt',
    primary_author: {
      name: 'Marián Čuprík',
      profile_image: null,
      slug: 'marian'
    },
    reading_time: 8,
    primary_tag: {
      name: 'Produktivita',
      slug: 'produktivita'
    },
    visibility: 'public'
  },
  {
    id: 'fallback-3',
    title: '5 najlepších AI nástrojov pre malé podniky',
    slug: '5-najlepsich-ai-nastrojov',
    excerpt: 'Aj malé podniky môžu profitovať z využitia umelej inteligencie. Predstavujeme 5 dostupných nástrojov, ktoré zvýšia vašu efektivitu...',
    html: '<p>Aj malé podniky môžu profitovať z využitia umelej inteligencie. Predstavujeme 5 dostupných nástrojov, ktoré zvýšia vašu efektivitu...</p><ol><li>ChatGPT - asistent pre komunikáciu</li><li>Midjourney - generovanie obrázkov</li><li>Claude - pokročilé spracovanie textu</li><li>GitHub Copilot - programovanie</li><li>Descript - editácia videa a audia</li></ol>',
    feature_image: 'https://images.unsplash.com/photo-1516110833967-0b5716ca1387?q=80&w=1000&auto=format&fit=crop',
    published_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    url: 'https://blog.ai-kurzy.online/5-najlepsich-ai-nastrojov',
    primary_author: {
      name: 'Marián Čuprík',
      profile_image: null,
      slug: 'marian'
    },
    reading_time: 6,
    primary_tag: {
      name: 'Biznis',
      slug: 'biznis'
    },
    visibility: 'public'
  },
  {
    id: 'fallback-4',
    title: 'Pokročilé AI techniky pre expertov',
    slug: 'pokrocile-ai-techniky-pre-expertov',
    excerpt: 'Exkluzívny obsah pre predplatiteľov: Pokročilé techniky strojového učenia, deep learning a najnovšie trendy v AI výskume...',
    html: '<p>Tento obsah je dostupný len pre platiacich predplatiteľov.</p>',
    feature_image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=1000&auto=format&fit=crop',
    published_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    url: 'https://blog.ai-kurzy.online/pokrocile-ai-techniky-pre-expertov',
    primary_author: {
      name: 'Marián Čuprík',
      profile_image: null,
      slug: 'marian'
    },
    reading_time: 12,
    primary_tag: {
      name: 'Premium',
      slug: 'premium'
    },
    visibility: 'paid',
    access: false
  }
];

// Debug mode flag - set to true for troubleshooting, false in production
const DEBUG_MODE = true; // Enable debugging to help troubleshoot 400 errors

// Simple debug logger that only logs in debug mode
const debug = (...args: any[]) => {
  if (DEBUG_MODE) {
    console.log(...args);
  }
};

// Helper to process images from Ghost API
const processImageUrl = (imageUrl: string | null | undefined): string | null => {
  if (!imageUrl) {
    debug('No image URL provided');
    return null;
  }
  
  debug('Processing image URL:', imageUrl);
  
  try {
    // Check if the URL is a data URL (base64 encoded image)
    if (imageUrl.startsWith('data:image/')) {
      debug('Image is a data URL, returning as is');
      return imageUrl;
    }
    
    // Fix for unsplash images with custom sizes
    if (imageUrl.includes('images.unsplash.com') && !imageUrl.includes('&w=')) {
      const enhancedUrl = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}w=800&fit=crop&auto=format`;
      debug('Enhanced Unsplash URL:', enhancedUrl);
      return enhancedUrl;
    }
    
    // If the URL is already absolute with http/https, apply domain fixes
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      // Most important fix: replace internal Ghost URLs with the public domain
      if (imageUrl.includes('ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io')) {
        // Handle images from internal Ghost storage specifically
        if (imageUrl.includes('/content/images/')) {
          // Extract the path after the domain
          const pathMatch = imageUrl.match(/\/content\/images\/(.+)/);
          if (pathMatch && pathMatch[1]) {
            const fixedUrl = `https://blog.ai-kurzy.online/content/images/${pathMatch[1]}`;
            debug('Fixed internal content URL:', fixedUrl);
            return fixedUrl;
          }
        }
        
        // For other URLs, just replace the domain
        const fixedUrl = imageUrl.replace(
          'ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io',
          'blog.ai-kurzy.online'
        );
        debug('Fixed sslip.io URL:', fixedUrl);
        return fixedUrl;
      }
      
      // For Unsplash images, ensure they have proper parameters
      if (imageUrl.includes('images.unsplash.com')) {
        // Only add parameters if they don't already exist
        if (!imageUrl.includes('w=') && !imageUrl.includes('&w=')) {
          const separator = imageUrl.includes('?') ? '&' : '?';
          const enhancedUrl = `${imageUrl}${separator}w=800&fit=crop&auto=format`;
          debug('Enhanced Unsplash URL:', enhancedUrl);
          return enhancedUrl;
        }
      }
      
      // Check if URL doesn't end with an image extension
      if (!/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(imageUrl)) {
        debug('URL lacks image extension, but is absolute. Using as is.');
      }
      
      return imageUrl;
    }
    
    // If it's a relative URL, prepend the blog domain
    if (imageUrl.startsWith('/')) {
      const fullUrl = `https://blog.ai-kurzy.online${imageUrl}`;
      debug('Converted relative URL to absolute:', fullUrl);
      return fullUrl;
    }
    
    // If it's a Ghost storage URL format
    if (imageUrl.includes('/content/images/')) {
      const fullUrl = imageUrl.startsWith('https://')
        ? imageUrl
        : `https://blog.ai-kurzy.online${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
      debug('Processed Ghost storage URL:', fullUrl);
      return fullUrl;
    }
    
    // Handle legacy Ghost URLs that might not start with slash
    if (!imageUrl.startsWith('/') && !imageUrl.startsWith('http')) {
      const fullUrl = `https://blog.ai-kurzy.online/${imageUrl}`;
      debug('Added domain to legacy URL:', fullUrl);
      return fullUrl;
    }
    
    // Return the original if none of the conditions match
    debug('Using original URL as fallback:', imageUrl);
    return imageUrl;
  } catch (error) {
    console.error('Error processing image URL:', error);
    return imageUrl; // Return original URL on error
  }
};

// Helper to process post URLs
const processPostUrl = (url: string): string => {
  // Check if URL exists
  if (!url) return 'https://blog.ai-kurzy.online';
  
  try {
    // Replace internal Ghost URLs with the public blog domain
    if (url.includes('ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io')) {
      // Extract the slug path from the URL
      let slug = '';
      
      // Try to extract the slug from the URL path
      const urlObj = new URL(url);
      slug = urlObj.pathname;
      
      // If we got a slug, build a clean URL
      if (slug && slug !== '/') {
        // Remove trailing slash if present
        if (slug.endsWith('/')) {
          slug = slug.slice(0, -1);
        }
        
        // If slug starts with slash, keep it as is, otherwise add one
        if (!slug.startsWith('/')) {
          slug = '/' + slug;
        }
        
        const cleanUrl = `https://blog.ai-kurzy.online${slug}`;
        debug(`Cleaned post URL: ${cleanUrl} (from ${url})`);
        return cleanUrl;
      }
      
      // Fallback to simple domain replacement
      return url.replace(
        'ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io',
        'blog.ai-kurzy.online'
      );
    }
    
    // If it's a relative URL, prepend the blog domain
    if (url.startsWith('/')) {
      return `https://blog.ai-kurzy.online${url}`;
    }
    
    // If it doesn't have a protocol, add https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    
    return url;
  } catch (error) {
    console.error('Error processing post URL:', error);
    // Fallback to a simple URL
    return url.replace(
      'ghost-b88ko48wg488g8ccsgg4kws8.167.235.31.245.sslip.io',
      'blog.ai-kurzy.online'
    );
  }
};

// API configuration
const API_BASE = 'https://blog.ai-kurzy.online/ghost/api/content/';
const API_URL = `${API_BASE}posts/`;
const TAGS_URL = `${API_BASE}tags/`;
const API_KEY = '52652912260f372c82b0851d48';

// Simple in-memory cache
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache

function getCachedData<T>(key: string): T | null {
  const item = cache.get(key);
  if (!item) return null;
  
  const now = Date.now();
  if (now - item.timestamp > CACHE_TTL) {
    // Cache expired
    cache.delete(key);
    return null;
  }
  
  return item.data as T;
}

function setCachedData(key: string, data: any): void {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
}

// Helper for processing posts
const processPost = (post: any): GhostPost => {
  // Ensure primary_tag exists to avoid errors
  const primaryTag = post.primary_tag || null;
  
  // Ensure primary_author exists to avoid errors
  const primaryAuthor = post.primary_author || {
    name: 'Marián Čuprík',
    profile_image: null
  };
  
  return {
    ...post,
    feature_image: processImageUrl(post.feature_image),
    feature_image_caption: post.feature_image_caption || '',
    url: processPostUrl(post.url),
    html: post.html || '', // Ensure HTML content exists
    visibility: post.visibility || 'public', // Default to public if not specified
    access: post.access !== undefined ? post.access : true, // Default to accessible
    primary_tag: primaryTag ? {
      name: primaryTag.name || 'Uncategorized',
      slug: primaryTag.slug
    } : null,
    primary_author: {
      ...primaryAuthor,
      profile_image: processImageUrl(primaryAuthor.profile_image)
    }
  };
};

// Server-side cache for meta data (separate from client cache)
const serverCache = new Map<string, { data: any; timestamp: number }>();
const SERVER_CACHE_TTL = 10 * 60 * 1000; // 10 minutes cache for server-side

function getServerCachedData<T>(key: string): T | null {
  const item = serverCache.get(key);
  if (!item) return null;
  
  const now = Date.now();
  if (now - item.timestamp > SERVER_CACHE_TTL) {
    serverCache.delete(key);
    return null;
  }
  
  return item.data as T;
}

function setServerCachedData(key: string, data: any): void {
  serverCache.set(key, {
    data,
    timestamp: Date.now()
  });
}

// Interface for minimal post data needed for meta tags
export interface PostMetaData {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featureImage: string | null;
  publishedAt: string;
  updatedAt?: string;
  author: {
    name: string;
    profileImage?: string;
  };
  primaryTag?: {
    name: string;
    slug: string;
  };
  url: string;
  readingTime?: number;
}

// Server-side Ghost service for edge functions and server environments
export const ghostServiceServer = {
  /**
   * Get minimal post data by slug for meta tag generation
   * Optimized for server-side use with timeout and caching
   */
  async getPostBySlugForMeta(slug: string): Promise<PostMetaData | null> {
    if (!slug) {
      debug('getPostBySlugForMeta called with empty slug');
      return null;
    }

    const sanitizedSlug = encodeURIComponent(slug.trim());
    debug(`Server: Fetching meta data for slug: ${sanitizedSlug}`);

    // Check server cache first
    const cacheKey = `server_meta_${sanitizedSlug}`;
    const cachedData = getServerCachedData<PostMetaData>(cacheKey);
    if (cachedData) {
      debug(`Server: Using cached meta data for slug: ${sanitizedSlug}`);
      return cachedData;
    }

    try {
      // Use direct slug API endpoint with minimal fields for performance
      const apiUrl = `${API_BASE}posts/slug/${sanitizedSlug}/`;
      debug(`Server: Making request to: ${apiUrl}`);

      const response = await axios.get<any>(apiUrl, {
        params: {
          key: API_KEY,
          fields: 'id,title,slug,excerpt,feature_image,published_at,updated_at,url,primary_author,primary_tag,reading_time'
        },
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
          'User-Agent': 'AI-Kurzy-Server/1.0'
        },
        timeout: 3000 // 3 second timeout for server-side calls
      });

      if (response.data?.posts?.length > 0) {
        const post = response.data.posts[0];
        debug(`Server: Found post: ${post.title}`);

        // Transform to PostMetaData format
        const metaData: PostMetaData = {
          id: post.id,
          title: post.title,
          slug: post.slug,
          excerpt: post.excerpt || '',
          featureImage: processImageUrl(post.feature_image),
          publishedAt: post.published_at,
          updatedAt: post.updated_at,
          author: {
            name: post.primary_author?.name || 'AI Kurzy',
            profileImage: processImageUrl(post.primary_author?.profile_image) || undefined
          },
          primaryTag: post.primary_tag ? {
            name: post.primary_tag.name,
            slug: post.primary_tag.slug
          } : undefined,
          url: processPostUrl(post.url),
          readingTime: post.reading_time
        };

        // Cache the result
        setServerCachedData(cacheKey, metaData);
        debug(`Server: Cached meta data for slug: ${sanitizedSlug}`);

        return metaData;
      }

      debug(`Server: No post found for slug: ${sanitizedSlug}`);
      return null;

    } catch (error: any) {
      console.error(`Server: Error fetching post meta data for slug "${sanitizedSlug}":`, error.message);

      // Try fallback with filter approach
      try {
        debug(`Server: Trying fallback approach for slug: ${sanitizedSlug}`);
        
        const fallbackResponse = await axios.get<any>(API_URL, {
          params: {
            key: API_KEY,
            filter: `slug:${sanitizedSlug}`,
            limit: 1,
            fields: 'id,title,slug,excerpt,feature_image,published_at,updated_at,url,primary_author,primary_tag,reading_time'
          },
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'AI-Kurzy-Server/1.0'
          },
          timeout: 3000
        });

        if (fallbackResponse.data?.posts?.length > 0) {
          const post = fallbackResponse.data.posts[0];
          debug(`Server: Found post via fallback: ${post.title}`);

          const metaData: PostMetaData = {
            id: post.id,
            title: post.title,
            slug: post.slug,
            excerpt: post.excerpt || '',
            featureImage: processImageUrl(post.feature_image),
            publishedAt: post.published_at,
            updatedAt: post.updated_at,
            author: {
              name: post.primary_author?.name || 'AI Kurzy',
              profileImage: processImageUrl(post.primary_author?.profile_image) || undefined
            },
            primaryTag: post.primary_tag ? {
              name: post.primary_tag.name,
              slug: post.primary_tag.slug
            } : undefined,
            url: processPostUrl(post.url),
            readingTime: post.reading_time
          };

          setServerCachedData(cacheKey, metaData);
          return metaData;
        }

      } catch (fallbackError: any) {
        console.error(`Server: Fallback also failed for slug "${sanitizedSlug}":`, fallbackError.message);
      }

      // Check if we have a matching fallback post
      const fallbackPost = fallbackPosts.find(post => 
        post.slug === sanitizedSlug || 
        post.slug.toLowerCase() === sanitizedSlug.toLowerCase()
      );

      if (fallbackPost) {
        debug(`Server: Using fallback post for slug: ${sanitizedSlug}`);
        const metaData: PostMetaData = {
          id: fallbackPost.id,
          title: fallbackPost.title,
          slug: fallbackPost.slug,
          excerpt: fallbackPost.excerpt,
          featureImage: fallbackPost.feature_image || null,
          publishedAt: fallbackPost.published_at,
          updatedAt: fallbackPost.updated_at,
          author: {
            name: fallbackPost.primary_author.name,
            profileImage: fallbackPost.primary_author.profile_image || undefined
          },
          primaryTag: fallbackPost.primary_tag ? {
            name: fallbackPost.primary_tag.name,
            slug: fallbackPost.primary_tag.slug || ''
          } : undefined,
          url: fallbackPost.url,
          readingTime: fallbackPost.reading_time
        };

        return metaData;
      }

      return null;
    }
  },

  /**
   * Get fallback meta data for error cases
   */
  getFallbackMetaData(slug?: string): PostMetaData {
    const title = slug ? 
      slug.split(/[-_]/g).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') :
      'AI Kurzy Blog';

    return {
      id: 'fallback',
      title: title,
      slug: slug || 'blog',
      excerpt: 'Kurzy o AI pre všetkých. Praktické školenia pre firmy a jednotlivcov.',
      featureImage: 'https://ai-kurzy.online/og-image-blog.jpg',
      publishedAt: new Date().toISOString(),
      author: {
        name: 'AI Kurzy',
        profileImage: undefined
      },
      url: slug ? `https://ai-kurzy.online/blog/${slug}` : 'https://ai-kurzy.online/blog',
      readingTime: 5
    };
  },

  /**
   * Clear server-side cache
   */
  clearServerCache(): void {
    serverCache.clear();
    debug('Server: Cache cleared');
  },

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: serverCache.size,
      keys: Array.from(serverCache.keys())
    };
  }
};

export const ghostService = {
  async getPosts(limit = 3): Promise<GhostApiResponse> {
    try {
      // Try to fetch from the Ghost Content API first
      debug(`Fetching Ghost posts from: ${API_URL} with key: ${API_KEY}`);
      
      // Add a timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      const response = await axios.get<any>(API_URL, {
        params: {
          key: API_KEY,
          limit: limit,
          include: 'authors,tags',
          fields: 'id,title,slug,html,excerpt,feature_image,feature_image_caption,featured,published_at,reading_time,url,primary_author,primary_tag,visibility,access',
          t: timestamp // Add timestamp to avoid caching
        },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 10000 // 10 second timeout
      });
      
      // Detailed logging of API response (only in debug mode)
      debug('Ghost API response status:', response.status);
      
      // Check for posts in the response
      const hasPosts = !!(response.data && response.data.posts && response.data.posts.length > 0);
      debug('Has posts in response:', hasPosts);
      
      if (hasPosts) {
        // Process image URLs in the response
        const processedPosts = response.data.posts.map((post: any, index: number) => {
          debug(`Processing post #${index+1}: ${post.id} - ${post.title}`);
          
          // Check for feature image issues
          if (!post.feature_image) {
            debug(`Post ${post.id} has no feature image!`);
          }
          
          // Ensure primary_tag exists to avoid errors
          const primaryTag = post.primary_tag || null;
          
          // Ensure primary_author exists to avoid errors
          const primaryAuthor = post.primary_author || {
            name: 'Marián Čuprík',
            profile_image: null
          };
          
          // Process the feature image URL
          const processedFeatureImage = processImageUrl(post.feature_image);
          
          const processedPost = {
            ...post,
            feature_image: processedFeatureImage,
            feature_image_caption: post.feature_image_caption || '',
            url: processPostUrl(post.url),
            html: post.html || '', // Ensure HTML content exists
            primary_tag: primaryTag ? {
              name: primaryTag.name || 'Uncategorized',
              slug: primaryTag.slug || primaryTag.name?.toLowerCase().replace(/\s+/g, '-') || 'uncategorized'
            } : null,
            primary_author: {
              ...primaryAuthor,
              profile_image: processImageUrl(primaryAuthor.profile_image),
              slug: primaryAuthor.slug || 'author'
            }
          };
          
          return processedPost;
        });
        
        debug('Processed posts count:', processedPosts.length);
        // ALWAYS return the real posts when we have them
        return { posts: processedPosts };
      } else {
        // Fall back to fallback posts only if no real posts found
        debug('No posts returned from Ghost API, using fallback posts');
        return { posts: fallbackPosts.slice(0, limit) };
      }
    } catch (error: any) {
      console.error('Error fetching posts from Ghost API:', error.message);
      
      // More detailed error logging
      if (error.isAxiosError && error.response) {
        console.error('API Error:', {
          status: error.response.status,
          statusText: error.response.statusText
        });
      }
      
      // Make another attempt with a simpler request
      try {
        debug('Making a second attempt with a simpler request configuration...');
        const simpleResponse = await axios.get<any>(API_URL, {
          params: {
            key: API_KEY,
            limit: limit
          },
          timeout: 15000 // Longer timeout for second attempt
        });
        
        if (simpleResponse.data && simpleResponse.data.posts && simpleResponse.data.posts.length > 0) {
          debug('Second attempt successful! Retrieved', simpleResponse.data.posts.length, 'posts');
          
          // Process posts with minimal transformations to avoid errors
          const simplePosts = simpleResponse.data.posts.map((post: any) => ({
            ...post,
            feature_image: processImageUrl(post.feature_image),
            feature_image_caption: post.feature_image_caption || '',
            url: processPostUrl(post.url),
            html: post.html || '',
            primary_author: post.primary_author || {
              name: 'Marián Čuprík',
              profile_image: null,
              slug: 'marian'
            },
            primary_tag: post.primary_tag ? {
              name: post.primary_tag.name || 'Uncategorized',
              slug: post.primary_tag.slug || 'uncategorized'
            } : null
          }));
          
          return { posts: simplePosts };
        }
      } catch (secondError) {
        console.error('Second attempt also failed');
      }
      
      debug('Using fallback posts after all attempts failed');
      // Use the fallback posts when the API call fails
      return { posts: fallbackPosts.slice(0, limit) };
    }
  },
  
  // Fetch a single post by slug
  async getPostBySlug(slug: string): Promise<GhostPost | null> {
    if (!slug) {
      console.error('getPostBySlug called with empty slug');
      return fallbackPosts[0]; // Return first fallback post if no slug provided
    }
    
    // Sanitize slug to prevent API errors
    const sanitizedSlug = encodeURIComponent(slug.trim());
    debug(`Original slug: "${slug}", Sanitized slug: "${sanitizedSlug}"`);
    
    // Check cache first
    const cacheKey = `post_${sanitizedSlug}`;
    const cachedPost = getCachedData<GhostPost>(cacheKey);
    if (cachedPost) {
      debug(`Using cached post data for slug: ${sanitizedSlug}`);
      return cachedPost;
    }
    
    // Multiple strategies to get a post by slug
    let post: GhostPost | null = null;
    
    // Strategy #1: Use direct URL access with simpler request configuration
    try {
      debug(`Strategy #1: Using direct URL access for slug: ${sanitizedSlug}`);
      // The correct Ghost API URL pattern for accessing by slug is /posts/slug/:slug/
      const apiUrl = `${API_BASE}posts/slug/${sanitizedSlug}/`;
      debug(`Making request to: ${apiUrl}`);
      
      const directUrlResponse = await axios.get<any>(apiUrl, {
        params: {
          key: API_KEY,
        },
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache',
        },
        timeout: 20000 // Increased timeout further
      });
      
      // Log full response details for debugging
      debug(`Strategy #1 response status: ${directUrlResponse.status}`);
      debug(`Strategy #1 response data keys: ${Object.keys(directUrlResponse.data || {}).join(', ')}`);
      debug(`Strategy #1 response has posts: ${!!directUrlResponse.data?.posts}`);
      
      if (directUrlResponse.data?.posts?.length > 0) {
        const matchingPost = directUrlResponse.data.posts[0];
        debug(`✅ Found post by direct URL access: ${matchingPost.title}`);
        
        // Ensure post has all required fields
        if (!matchingPost.feature_image_caption) {
          debug('Adding missing feature_image_caption');
          matchingPost.feature_image_caption = '';
        }
        
        post = processPost(matchingPost);
        setCachedData(cacheKey, post);
        return post;
      } else {
        debug('No posts found in response data. Full response:');
        debug(JSON.stringify(directUrlResponse.data, null, 2).substring(0, 500) + '...');
      }
    } catch (error: any) {
      console.error(`Strategy #1 failed:`, error.message);
      debug(`Error type: ${error.name}, Code: ${error.code}`);
      
      // Check if this is an Axios error by looking for typical Axios error properties
      if (error.response) {
        debug(`Response status: ${error.response.status}`);
        debug(`Response data: ${JSON.stringify(error.response.data || {}, null, 2).substring(0, 500)}`);
        if (error.config) {
          debug(`Request URL: ${error.config.url}`);
          debug(`Request params: ${JSON.stringify(error.config.params || {})}`);
        }
      }
      // Continue to next strategy
    }
    
    // Strategy #2: Simple basic request with filter parameter
    try {
      debug(`Strategy #2: Simple basic request for slug: ${sanitizedSlug}`);
      
      const simpleResponse = await axios.get<any>(API_URL, {
        params: {
          key: API_KEY,
          limit: 1,
          include: 'authors,tags',
          filter: `slug:${sanitizedSlug}`,
          fields: 'id,title,slug,html,excerpt,feature_image,feature_image_caption,published_at,updated_at,reading_time,url,primary_author,primary_tag,tags'
        },
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 15000
      });
      
      debug(`Strategy #2 response status: ${simpleResponse.status}`);
      debug(`Strategy #2 response has posts: ${!!simpleResponse.data?.posts}`);
      
      if (simpleResponse.data?.posts?.length > 0) {
        const matchingPost = simpleResponse.data.posts[0]; // Should be the only one
        debug(`✅ Found post by direct API request: ${matchingPost.title}`);
        debug(`HTML content available: ${!!matchingPost.html}, length: ${matchingPost.html?.length || 0}`);
        
        post = processPost(matchingPost);
        setCachedData(cacheKey, post);
        return post;
      } else {
        debug(`⚠️ No posts returned from direct API request for slug: ${slug}`);
      }
    } catch (error: any) {
      console.error(`Strategy #2 failed:`, error.message);
      if (error.response) {
        debug('Response status:', error.response.status);
      }
    }
    
    // Strategy #3: Get all posts and filter client-side
    try {
      debug(`Strategy #3: Fetching all posts to find slug: ${sanitizedSlug}`);
      const response = await axios.get<any>(API_URL, {
        params: {
          key: API_KEY,
          limit: 'all', // Try to get all posts
          include: 'authors,tags',
          fields: 'id,title,slug,html,excerpt,feature_image,feature_image_caption,published_at,reading_time,url,primary_author,primary_tag,tags',
        },
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 20000
      });
      
      if (response.data?.posts?.length > 0) {
        debug(`Got ${response.data.posts.length} posts, filtering for slug: ${sanitizedSlug}`);
        
        // First try exact match
        let matchingPost = response.data.posts.find((p: any) => p.slug === sanitizedSlug);
        
        // If no exact match, try case-insensitive match
        if (!matchingPost) {
          debug('No exact match found, trying case-insensitive match');
          matchingPost = response.data.posts.find(
            (p: any) => p.slug.toLowerCase() === sanitizedSlug.toLowerCase()
          );
        }
        
        // If still no match, try to find a post that contains the slug
        if (!matchingPost) {
          debug('No case-insensitive match found, trying partial match');
          matchingPost = response.data.posts.find(
            (p: any) => p.slug.includes(sanitizedSlug) || sanitizedSlug.includes(p.slug)
          );
        }
        
        if (matchingPost) {
          debug(`✅ Found post by slug in all posts: ${matchingPost.title}`);
          post = processPost(matchingPost);
          setCachedData(cacheKey, post);
          return post;
        } else {
          debug(`⚠️ Post with slug "${sanitizedSlug}" not found in all posts`);
          debug('Available slugs: ' + response.data.posts.map((p: any) => p.slug).join(', ').substring(0, 500));
        }
      }
    } catch (error: any) {
      console.error(`Strategy #3 failed:`, error.message);
      if (error.response) {
        debug('Response status:', error.response.status);
      }
    }
    
    // Last resort: Check fallback posts
    debug('All API strategies failed, checking fallback posts for slug:', sanitizedSlug);
    
    // Try exact match first
    let fallbackPost = fallbackPosts.find(post => post.slug === sanitizedSlug);
    
    // If no exact match, try case-insensitive match
    if (!fallbackPost) {
      fallbackPost = fallbackPosts.find(post => post.slug.toLowerCase() === sanitizedSlug.toLowerCase());
    }
    
    // If still no match, try partial match
    if (!fallbackPost) {
      fallbackPost = fallbackPosts.find(post =>
        post.slug.includes(sanitizedSlug) || sanitizedSlug.includes(post.slug)
      );
    }
    
    if (fallbackPost) {
      debug('✅ Found matching fallback post:', fallbackPost.title);
      return fallbackPost;
    }
    
    // Create a custom fallback post with the requested slug
    debug('⚠️ No post found with slug, creating custom fallback post with requested slug');
    
    // Create a readable title from the slug
    const titleFromSlug = sanitizedSlug
      .split(/[-_]/g)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    const customFallbackPost: GhostPost = {
      ...fallbackPosts[0],
      id: `custom-${sanitizedSlug}`,
      title: titleFromSlug || 'Requested Article',
      slug: slug,
      excerpt: 'We were unable to retrieve the requested article content. Please try again later.',
      html: `<div class="error-container">
        <h2>Article Not Found</h2>
        <p>We were unable to retrieve the requested article content. This might be because:</p>
        <ul>
          <li>The article URL may have changed</li>
          <li>The article may have been removed</li>
          <li>There might be a temporary issue with our blog service</li>
        </ul>
        <p>Please try again later or browse our other articles.</p>
      </div>`,
      feature_image: 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=800&auto=format',
      feature_image_caption: 'Article not found',
      url: `https://blog.ai-kurzy.online/${slug}`
    };
    
    return customFallbackPost;
  },
  
  // Fetch posts with pagination support
  async getPaginatedPosts(page = 1, limit = 9): Promise<GhostApiResponse> {
    // Check cache first
    const cacheKey = `posts_page${page}_limit${limit}`;
    const cachedData = getCachedData<GhostApiResponse>(cacheKey);
    if (cachedData) {
      debug(`Using cached paginated posts for page ${page}`);
      return cachedData;
    }
    
    try {
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      const response = await axios.get<any>(API_URL, {
        params: {
          key: API_KEY,
          page: page,
          limit: limit,
          include: 'authors,tags',
          fields: 'id,title,slug,html,excerpt,feature_image,feature_image_caption,featured,published_at,reading_time,url,primary_author,primary_tag,visibility,access',
          t: timestamp
        },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 10000
      });
      
      if (response.data && response.data.posts) {
        const processedPosts = response.data.posts.map((post: any) => processPost(post));
        
        const result = {
          posts: processedPosts,
          meta: response.data.meta || {}
        };
        
        // Cache the result
        setCachedData(cacheKey, result);
        
        return result;
      }
      
      return { posts: [] };
    } catch (error: any) {
      console.error('Error fetching paginated posts:', error.message);
      
      // Fallback to regular posts if pagination fails
      try {
        debug('Falling back to regular getPosts method');
        const result = await this.getPosts(limit);
        return {
          posts: result.posts,
          meta: {
            pagination: {
              page: 1,
              limit: limit,
              pages: 1,
              total: result.posts.length
            }
          }
        };
      } catch (fallbackError) {
        console.error('Fallback to regular posts also failed');
        return { posts: fallbackPosts.slice(0, limit) };
      }
    }
  },
  
  // Fetch posts by tag
  async getPostsByTag(tag: string, page = 1, limit = 9): Promise<GhostApiResponse> {
    // Check cache first
    const cacheKey = `posts_tag${tag}_page${page}_limit${limit}`;
    const cachedData = getCachedData<GhostApiResponse>(cacheKey);
    if (cachedData) {
      debug(`Using cached posts for tag ${tag}, page ${page}`);
      return cachedData;
    }
    
    try {
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      const response = await axios.get<any>(API_URL, {
        params: {
          key: API_KEY,
          filter: `tag:${tag}`,
          page: page,
          limit: limit,
          include: 'authors,tags',
          fields: 'id,title,slug,html,excerpt,feature_image,feature_image_caption,featured,published_at,reading_time,url,primary_author,primary_tag,visibility,access',
          t: timestamp
        },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 10000
      });
      
      if (response.data && response.data.posts) {
        const processedPosts = response.data.posts.map((post: any) => processPost(post));
        
        const result = {
          posts: processedPosts,
          meta: response.data.meta || {}
        };
        
        // Cache the result
        setCachedData(cacheKey, result);
        
        return result;
      }
      
      return { posts: [] };
    } catch (error: any) {
      console.error(`Error fetching posts by tag "${tag}":`, error.message);
      return { posts: [] };
    }
  },
  
  // Get all tags
  async getTags(): Promise<GhostTag[]> {
    // Check cache first
    const cacheKey = 'all_tags';
    const cachedTags = getCachedData<GhostTag[]>(cacheKey);
    if (cachedTags) {
      debug('Using cached tags data');
      return cachedTags;
    }
    
    try {
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      const response = await axios.get<any>(TAGS_URL, {
        params: {
          key: API_KEY,
          limit: 'all',
          include: 'count.posts',
          t: timestamp
        },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 10000
      });
      
      if (response.data && response.data.tags) {
        const tags = response.data.tags.map((tag: any) => ({
          id: tag.id,
          name: tag.name,
          slug: tag.slug,
          description: tag.description || null,
          feature_image: tag.feature_image ? processImageUrl(tag.feature_image) : null,
          count: tag.count || { posts: 0 }
        }));
        
        // Cache the result
        setCachedData(cacheKey, tags);
        
        return tags;
      }
      
      return [];
    } catch (error: any) {
      console.error('Error fetching tags:', error.message);
      return [];
    }
  },
  
  // Search posts
  async searchPosts(query: string, page = 1, limit = 9): Promise<GhostApiResponse> {
    // Check cache first
    const cacheKey = `search_${query}_page${page}_limit${limit}`;
    const cachedData = getCachedData<GhostApiResponse>(cacheKey);
    if (cachedData) {
      debug(`Using cached search results for "${query}", page ${page}`);
      return cachedData;
    }
    
    try {
      // Add timestamp to prevent caching issues
      const timestamp = new Date().getTime();
      
      const response = await axios.get<any>(API_URL, {
        params: {
          key: API_KEY,
          search: query,
          page: page,
          limit: limit,
          include: 'authors,tags',
          fields: 'id,title,slug,html,excerpt,feature_image,feature_image_caption,featured,published_at,reading_time,url,primary_author,primary_tag,visibility,access',
          t: timestamp
        },
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        timeout: 10000
      });
      
      if (response.data && response.data.posts) {
        const processedPosts = response.data.posts.map((post: any) => processPost(post));
        
        const result = {
          posts: processedPosts,
          meta: response.data.meta || {}
        };
        
        // Cache the result
        setCachedData(cacheKey, result);
        
        return result;
      }
      
      return { posts: [] };
    } catch (error: any) {
      console.error(`Error searching posts for "${query}":`, error.message);
      return { posts: [] };
    }
  },
  
  // Clear cache
  clearCache(): void {
    cache.clear();
    debug('Ghost service cache cleared');
  }
};